import { useFusionAuth } from "@fusionauth/react-sdk";
import { ExpandLess, ExpandMore } from "@mui/icons-material";
import CloseIcon from "@mui/icons-material/Close";
import {
  Button,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  Checkbox,
  CircularProgress,
  Collapse,
  FormControlLabel,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Popover,
  Typography,
} from "@mui/material";
import React, { useContext, useEffect, useMemo, useState } from "react";
import { ProjectContext } from "../../contexts/ProjectContext";
import { useFetch } from "../../services/hooks/useFetch";
import { exportData, type ExportFile } from "../../services/ProjectsServices";

import { apiPrefix } from "../../utils/main";

const FILE_EXPORT_OPTIONS: {
  [key: string]:
    | {
        label: string;
        files: ExportFile[];
      }
    | undefined;
} = {
  categorizations: {
    label: "Categorizations",
    files: [
      {
        id: "ADVERSE_EVENTS_DETAILS",
        label: "Adverse Events Details",
      },
      {
        id: "CLINICAL_BENEFIT_DETAILS",
        label: "Clinical Benefit Details",
      },
      {
        id: "COMORBIDITY_DETAILS",
        label: "Comorbidity Details",
      },
      {
        id: "DEVICE_DEFICIENCY_DETAILS",
        label: "Device Deficiency Details",
      },
      {
        id: "INDICATION_FOR_USE_DETAILS",
        label: "Indication for Use Details",
      },
      {
        id: "MEDICATIONS_DETAILS",
        label: "Medications Details",
      },
      {
        id: "PERFORMANCE_DETAILS",
        label: "Performance Details",
      },
      {
        id: "TARGET_LOCATION_DETAILS",
        label: "Target Location Details",
      },
    ],
  },
  patientData: {
    label: "Patient Data",
    files: [
      {
        id: "FLOWSHEET_DETAILS",
        label: "Flowsheet Data Listing",
        requires: "PROJECT_FLOWSHEET_GROUPS",
      },
      {
        id: "LAB_DETAILS",
        label: "Lab Data Listing",
        requires: "PROJECT_LAB_GROUPS",
      },
      {
        id: "OTHER_DEVICES_USED",
        label: "Other Devices Used Listing",
      },
      {
        id: "PATIENT_DETAILS",
        label: "Patient Details",
      },
      {
        id: "PATIENT_PROBLEMS",
        label: "Patient Problems Listing",
      },
      {
        id: "PROCEDURE_TIMELINE",
        label: "Patient Procedure Timeline Details",
        requires: "ENABLE_TIMELINE",
      },
      {
        id: "PATIENT_REPORTED_OUTCOME_CALCULATIONS",
        label: "Patient Reported Outcome Calculations",
        requires: "PROJECT_PROM_GROUPS",
      },
      {
        id: "PATIENT_REPORTED_OUTCOME_DETAILS",
        label: "Patient Reported Outcome Details",
        requires: "PROJECT_PROM_GROUPS",
      },
    ],
  },
  projectData: {
    label: "Project Data",
    files: [
      {
        id: "PROJECT_ACTIVITY_LOG",
        label: "Project Activity History",
      },
    ],
  },
};

interface DataExportDrawerInput {
  onClose: () => void;
  anchorEl: (EventTarget & HTMLButtonElement) | undefined;
}

export default function DataExportDrawer({
  onClose,
  anchorEl,
}: DataExportDrawerInput) {
  const [loading, setLoading] = useState(false);
  const { id: projectId, projectSettings: settings } =
    useContext(ProjectContext) || {};
  const {
    data: projectFlowsheetGroups,
    fetch: fetchProjectFlowsheetGroups,
    isLoading: projectFlowsheetGroupsLoading,
  } = useFetch<ProjectFlowsheetGroup[]>();
  const {
    data: projectLabGroups,
    fetch: fetchProjectLabGroups,
    isLoading: projectLabGroupsLoading,
  } = useFetch<ProjectLabGroup[]>();
  const {
    data: projectPromGroups,
    fetch: fetchProjectPromGroups,
    isLoading: projectPromGroupsLoading,
  } = useFetch<ProjectPromGroup[]>();

  const { attributeTypes } = useContext(ProjectContext)!;

  useEffect(() => {
    if (!projectFlowsheetGroupsLoading && !projectFlowsheetGroups) {
      void fetchProjectFlowsheetGroups(
        `${apiPrefix}/projects/${projectId}/flowsheets`,
      );
    }
  }, [
    fetchProjectFlowsheetGroups,
    projectFlowsheetGroups,
    projectFlowsheetGroupsLoading,
    projectId,
  ]);

  useEffect(() => {
    if (!projectLabGroupsLoading && !projectLabGroups) {
      void fetchProjectLabGroups(`${apiPrefix}/projects/${projectId}/labs`);
    }
  }, [
    fetchProjectLabGroups,
    projectLabGroups,
    projectLabGroupsLoading,
    projectId,
  ]);

  useEffect(() => {
    if (!projectPromGroups && !projectPromGroupsLoading) {
      void fetchProjectPromGroups(`${apiPrefix}/projects/${projectId}/proms`);
    }
  }, [
    projectPromGroups,
    projectPromGroupsLoading,
    fetchProjectPromGroups,
    projectId,
  ]);

  const fileExportOptions = useMemo(() => {
    if (attributeTypes) {
      const attributeTypeFiles: ExportFile[] = [];

      for (const attributeType of attributeTypes) {
        if (attributeType.projectId && attributeType.usageCount > 0) {
          attributeTypeFiles.push({
            id: attributeType.id,
            label: `${attributeType.name} Details`,
            customCategory: true,
          });
        }
      }

      const localFileExportOptions = structuredClone(FILE_EXPORT_OPTIONS);
      localFileExportOptions["categorizations"]?.files.push(
        ...attributeTypeFiles,
      );
      return localFileExportOptions;
    } else {
      return FILE_EXPORT_OPTIONS;
    }
  }, [attributeTypes]);

  function project_meets_requirement(
    requirement_name: string | undefined,
  ): boolean {
    if (requirement_name === undefined || requirement_name === "NONE") {
      return true;
    }
    if (requirement_name === "PROJECT_FLOWSHEET_GROUPS") {
      return !!projectFlowsheetGroups?.length;
    }
    if (requirement_name === "PROJECT_LAB_GROUPS") {
      return !!projectLabGroups?.length;
    }
    if (requirement_name === "PROJECT_PROM_GROUPS") {
      return !!projectPromGroups?.length;
    }

    const project_settings = settings || [];
    for (const setting of project_settings.filter(
      (s) => s.name === requirement_name,
    )) {
      if (setting?.value !== "") {
        return true;
      }
    }
    return false;
  }

  const { startLogout } = useFusionAuth();

  const ALL_POSSIBLE_FILES = new Set<ExportFile>();
  const initialSections: {
    [key: string]: {
      [key: string]: boolean;
    };
  } = {};
  Object.entries(fileExportOptions).map(([key, value]) => {
    initialSections[key] = {
      expanded: true,
      selected: false,
      indeterminate: false,
    };
    value?.files.map((f) => {
      if (project_meets_requirement(f.requires)) {
        ALL_POSSIBLE_FILES.add(f);
      }
    });
  });

  const [sections, setSections] = useState(initialSections);
  const [allFilesSelected, setAllFilesSelected] = useState(false);
  const [exportFiles, setExportFiles] = useState<Set<ExportFile>>(new Set());

  function onSubmit() {
    setLoading(true);
    exportData(projectId ?? "", Array.from(exportFiles))
      .then(() => {
        setLoading(false);
        onClose();
      })
      .catch((err) => {
        if (err === "401" || err === "403") {
          void startLogout();
        }
        setLoading(false);
      });
  }

  function toggleAllFilesSelected() {
    const selected = !allFilesSelected;
    setExportFiles(new Set(selected ? ALL_POSSIBLE_FILES : []));
  }

  function updateSections(section: string, key: string, value: boolean) {
    const updatedSection: { [key: string]: boolean } = sections[section];
    updatedSection[key] = value;
    if (key === "selected") {
      const newFiles = fileExportOptions[section]?.files.filter((f) =>
        project_meets_requirement(f.requires),
      );
      const updatedFiles = value
        ? new Set([...exportFiles, ...(newFiles ?? [])])
        : new Set(
            [...exportFiles].filter(
              (f) => !(newFiles?.map((nf) => nf.id) ?? []).includes(f.id),
            ),
          );
      setExportFiles(updatedFiles);
    }
    setSections({ ...sections, [section]: updatedSection });
  }

  function updateOptionalFiles(_section: string, file: ExportFile) {
    const localFiles = new Set(exportFiles);
    if (localFiles.has(file)) {
      localFiles.delete(file);
    } else {
      localFiles.add(file);
    }
    setExportFiles(localFiles);
  }

  useEffect(() => {
    const updatedSections = { ...sections };
    Object.keys(updatedSections).forEach((key) => {
      const sectionFiles = new Set(
        fileExportOptions[key]?.files.filter((f) =>
          project_meets_requirement(f.requires),
        ),
      );
      updatedSections[key].selected =
        exportFiles.size > 0 && exportFiles.isSupersetOf(sectionFiles);
      const fileCountDifference = sectionFiles.difference(exportFiles).size;
      updatedSections[key].indeterminate =
        exportFiles.size > 0 &&
        fileCountDifference > 0 &&
        fileCountDifference < sectionFiles.size;
    });
    setSections(updatedSections);
    setAllFilesSelected(exportFiles.size === ALL_POSSIBLE_FILES.size);
    // eslint-disable-next-line
  }, [ALL_POSSIBLE_FILES.size, exportFiles]);

  return (
    <Popover
      anchorEl={anchorEl}
      open={true}
      onClose={onClose}
      anchorOrigin={{
        vertical: "bottom",
        horizontal: "left",
      }}
      transformOrigin={{
        vertical: "top",
        horizontal: "left",
      }}
    >
      <Card className="export-modal">
        <CardHeader
          className="export-modal-header"
          title="Generate Data Files"
          subheader="Include the following files"
          action={
            <IconButton
              onClick={onClose}
              sx={{ "&:hover": { bgcolor: "action.hover" } }}
            >
              <CloseIcon />
            </IconButton>
          }
        />
        <CardContent className="export-modal-content">
          <Paper elevation={1} className="export-modal-selectAll">
            <FormControlLabel
              className="selectAll-label"
              label={
                <Typography fontWeight={"bold"}>
                  Export All Files (select all below)
                </Typography>
              }
              control={
                <Checkbox
                  checked={allFilesSelected}
                  indeterminate={
                    exportFiles.size > 0 &&
                    exportFiles.size < ALL_POSSIBLE_FILES.size
                  }
                  onChange={() => toggleAllFilesSelected()}
                />
              }
            />
          </Paper>
          <List className="export-modal-file-list">
            {Object.entries(fileExportOptions).map(([section, details]) => {
              return (
                <div key={section}>
                  <ListItem key={section} disablePadding>
                    {sections[section].expanded ? (
                      <ExpandLess
                        onClick={() =>
                          updateSections(section, "expanded", false)
                        }
                      />
                    ) : (
                      <ExpandMore
                        onClick={() =>
                          updateSections(section, "expanded", true)
                        }
                      />
                    )}
                    <ListItemIcon sx={{ minWidth: "inherit" }}>
                      <Checkbox
                        checked={sections[section].selected}
                        indeterminate={sections[section].indeterminate}
                        onChange={(_event, checked) =>
                          updateSections(section, "selected", checked)
                        }
                      />
                    </ListItemIcon>
                    <ListItemText
                      primary={details?.label}
                      primaryTypographyProps={{ fontWeight: "bold" }}
                    />
                  </ListItem>
                  <Collapse in={sections[section].expanded} unmountOnExit>
                    <List disablePadding>
                      {details?.files.map((value) => {
                        return (
                          project_meets_requirement(value.requires) && (
                            <ListItem
                              key={value.id}
                              sx={{ pl: 8 }}
                              disablePadding
                            >
                              <ListItemIcon sx={{ minWidth: "inherit" }}>
                                <Checkbox
                                  checked={exportFiles.has(value)}
                                  onChange={() =>
                                    updateOptionalFiles(section, value)
                                  }
                                />
                              </ListItemIcon>
                              <ListItemText primary={value.label} />
                            </ListItem>
                          )
                        );
                      })}
                    </List>
                  </Collapse>
                </div>
              );
            })}
          </List>
        </CardContent>
        <CardActions className="export-modal-footer">
          <Button onClick={onClose} variant={"outlined"} size={"small"}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant={"contained"}
            className={"categorize-button"}
            onClick={onSubmit}
            size={"small"}
            endIcon={loading && <CircularProgress size="1.5rem" />}
            disabled={loading || exportFiles.size === 0}
          >
            Generate Data Files
          </Button>
        </CardActions>
      </Card>
    </Popover>
  );
}
